import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import * as yup from "yup";

import useYupValidation from "../../../../../hooks/useYupValidation";
import { DecimalFormatType } from "../../../../../util/formatDecimalValue";
import { getMeasurement, getWeightMeasurement } from "../../../../../util/getMeasurement";
import { Input } from "../../../../Form";
import InputMeasurement from "../../../../Form/InputMeasurement";
import Modal from "../../../../Generic/Modal";
import { useBookingContext } from "../../../context/Booking";
import useStyles from "./styles";

export interface EditCartonHandler {
  open: (bookedValues: CartonValues, cartonValues?: CartonValues) => void;
  close: () => void;
}

interface Props {
  onEditTotalValue: (cartonValues: CartonValues) => void;
  onCancel?: () => void;
}

export interface CartonValues {
  sscc?: string;
  cbm: number;
  weight: number;
  quantity: number;
}

const EditCartonModal: ForwardRefRenderFunction<EditCartonHandler, Props> = (props, ref) => {
  const { onEditTotalValue } = props;
  const { setHasConfirmedCBMAndWeight } = useBookingContext();

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [modalOpened, setModalOpened] = useState(false);
  const [cartonValues, setCartonValues] = useState<CartonValues>({} as CartonValues);

  const [values, setValues] = useState<CartonValues>({} as CartonValues);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const validateTolerance = (prop: keyof CartonValues, _: number) => {
    if (!cartonValues || !cartonValues[prop]) return false;

    return true;

    // const MAX_PERCENTAGE_TOLERANCE = 10;
    // const difference = Math.abs(Number(cartonValues[prop]) - value);

    // const differencePercentage = (difference * 100) / Number(cartonValues[prop]);

    // if (differencePercentage <= MAX_PERCENTAGE_TOLERANCE) return true;

    // return false;
  };

  const schema = yup.object().shape({
    cbm: yup
      .number()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired"))
      .moreThan(0, translate("valueMustBeGreaterThanZero"))
      .test("tolerance", translate("valueMustBeWithin10OfSupplierValue"), (cbm) =>
        validateTolerance("cbm", Number(cbm))
      ),
    weight: yup
      .number()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired"))
      .moreThan(0, translate("valueMustBeGreaterThanZero"))
      .test("tolerance", translate("valueMustBeWithin10OfSupplierValue"), (weight) =>
        validateTolerance("weight", Number(weight))
      ),
    quantity: yup
      .number()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired"))
      .moreThan(0, translate("valueMustBeGreaterThanZero"))
      .test(
        "tolerance",
        `${translate("valueMustBeEqualToTheSupplierValue")} (${cartonValues?.quantity})`,
        (quantity) => cartonValues?.quantity === Number(quantity)
      ),
  });

  const handleValidate = (): boolean => {
    try {
      schema.validateSync(
        {
          cbm: values.cbm,
          weight: values.weight,
          quantity: values.quantity,
        },
        { abortEarly: false }
      );
      setErrors([]);
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }

    return true;
  };

  const handleUpdateValues = (prop: keyof CartonValues, value: number) => {
    setValues({ ...values, [prop]: value });
  };

  const openModal = useCallback((bookedValues: CartonValues, cartonValues?: CartonValues) => {
    if (bookedValues && cartonValues) {
      setCartonValues(bookedValues);
      setValues({
        cbm: Number(cartonValues?.cbm) || 0,
        weight: Number(cartonValues?.weight) || 0,
        quantity: Number(cartonValues?.quantity) || 0,
      });
    }

    setModalOpened(true);
  }, []);

  const closeModal = useCallback(() => {
    setErrors([]);
    setCartonValues({} as CartonValues);
    setValues({} as CartonValues);
    setModalOpened(false);
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  const handleSave = () => {
    if (!values) return;

    if (!handleValidate()) return;

    onEditTotalValue(values);
    setHasConfirmedCBMAndWeight(true);
    setModalOpened(false);
  };

  return (
    <Modal
      open={modalOpened}
      handleClose={closeModal}
      showModalFooter
      showActionButtons
      actionButtonProps={{ onCancel: closeModal, onSave: handleSave }}
      title={translate("insertTotalValue")}
    >
      <div className={classes.mainDiv}>
        <span className={classes.subtitle}>{translate("defineTotalValueOfSSCCs")}</span>
        <div className={classes.row}>
          <InputMeasurement
            fullWidth
            name="sscc-cbm"
            min="0"
            value={values?.cbm}
            change={(value: number) => handleUpdateValues("cbm", value)}
            label={`${translate("totalVolume")}`}
            error={fieldHasError("cbm")}
            errorMessage={getErrorByField("cbm")}
            measurement={measurement}
            decimalPlacesCustom={DecimalFormatType.MAERSK}
          />
          <InputMeasurement
            fullWidth
            name="sscc-weight"
            min="0"
            value={values?.weight}
            change={(value: number) => handleUpdateValues("weight", value)}
            label={`${translate("totalWeight")}`}
            error={fieldHasError("weight")}
            errorMessage={getErrorByField("weight")}
            measurement={measurementWeight}
            decimalPlacesCustom={DecimalFormatType.MAERSK}
          />
          <Input
            fullWidth
            name="sscc-quantity"
            type="number"
            min="0"
            onKeyPress={(event) => {
              if (!/[0-9]/.test(event.key)) {
                event.preventDefault();
              }
            }}
            value={String(values.quantity)}
            onChange={(event) => handleUpdateValues("quantity", Number(event.target.value))}
            label={translate("quantity")}
            error={fieldHasError("quantity")}
            errorMessage={getErrorByField("quantity")}
          />
        </div>
      </div>
    </Modal>
  );
};

export default forwardRef(EditCartonModal);
